import React from 'react';
import { formatCount } from './format';

export interface DayPoint { day: string; players: number }

const BAR_SLOT = 10;
const BAR_GAP = 2;
const PLOT_HEIGHT = 120;

const playersLabel = (count: number) => `${count} ${count === 1 ? 'player' : 'players'}`;

/**
 * Daily active players as a hand-rolled SVG bar chart (research R-3: one simple
 * chart does not justify a chart library). Hover shows each bar's `<title>`;
 * screen readers get the same rows as a visually hidden table.
 */
const DailyPlayersChart: React.FC<{ days: DayPoint[] }> = ({ days }) => {
  const max = days.reduce((peak, point) => Math.max(peak, point.players), 0);
  const width = Math.max(days.length, 1) * BAR_SLOT;

  return (
    <figure className="dau-chart">
      <div className="dau-plot">
        <span className="dau-peak" aria-hidden="true">{max > 0 ? `Peak ${formatCount(max)}` : ''}</span>
        <svg
          className="dau-svg"
          viewBox={`0 0 ${width} ${PLOT_HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          {days.map((point, index) => {
            const height = max > 0 ? Math.max(point.players > 0 ? 1.5 : 0, (point.players / max) * PLOT_HEIGHT) : 0;
            const x = index * BAR_SLOT;
            return (
              <g key={point.day}>
                <title>{`${point.day}: ${playersLabel(point.players)}`}</title>
                <rect className="dau-hit" x={x} y={0} width={BAR_SLOT} height={PLOT_HEIGHT} />
                <rect className="dau-bar" x={x + BAR_GAP / 2} y={PLOT_HEIGHT - height} width={BAR_SLOT - BAR_GAP} height={height} rx={1} />
              </g>
            );
          })}
        </svg>
      </div>
      {days.length > 0 && (
        <figcaption className="dau-axis" aria-hidden="true">
          <span>{days[0].day}</span>
          <span>{days[days.length - 1].day}</span>
        </figcaption>
      )}
      <div className="sr-only">
        <table>
          <caption>Daily active players</caption>
          <thead><tr><th scope="col">Day</th><th scope="col">Players</th></tr></thead>
          <tbody>
            {days.map(point => (
              <tr key={point.day}><th scope="row">{point.day}</th><td>{formatCount(point.players)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
};

export default DailyPlayersChart;
